import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@carbon/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from 'components/page-header';
import { DataTable, type TableState } from 'components/data-table';
import { PlainLink } from 'components/link';
import { parseUTCdateTime } from 'utils/dates';
import {
  useEvaluationIdFilter,
  useLegacyUnitEvaluationDownloads,
} from './hooks';
import styles from './page.module.scss';
import type { LegacyUnitEvaluationDownload } from './types';

const INITIAL_PAGE_SIZE = 50;

const TYPE_LABELS: Record<LegacyUnitEvaluationDownload['type'], string> = {
  cashflows_by_slot: 'Cashflows by Slot',
  evaluation_detail: 'Evaluation Detail',
};

// Labels for the statuses a download moves through; anything else shows as stored.
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  processing: 'Processing',
  processed: 'Completed',
  failed: 'Failed',
};

const columnHelper = createColumnHelper<LegacyUnitEvaluationDownload>();

const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: 'ID',
    size: 100,
    meta: { type: 'text', align: 'left' as const },
  }),
  columnHelper.accessor('type', {
    id: 'type',
    header: 'Type',
    size: 170,
    cell: (info) => TYPE_LABELS[info.getValue()] ?? info.getValue(),
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created',
    size: 190,
    cell: (info) => {
      const value = info.getValue();
      return value ? parseUTCdateTime(value, true) : '--';
    },
  }),
  columnHelper.accessor('createdBy', {
    id: 'createdBy',
    header: 'Requested By',
    size: 200,
    meta: { wrap: true },
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('unitName', {
    id: 'unitName',
    header: 'Unit',
    size: 200,
    meta: { wrap: true },
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('evaluationId', {
    id: 'evaluationId',
    header: 'Unit Evaluation',
    size: 150,
    meta: { type: 'text', align: 'left' as const },
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 120,
    cell: (info) => STATUS_LABELS[info.getValue()] ?? info.getValue(),
  }),
  columnHelper.accessor('fileUrl', {
    id: 'fileUrl',
    header: 'File',
    size: 140,
    enableSorting: false,
    cell: (info) => {
      const url = info.getValue();
      const { id, type, unitName } = info.row.original;
      // Starts with the visible text so speech input can still say "Open file".
      const label = `Open file: ${TYPE_LABELS[type] ?? type} for ${
        unitName ?? 'unit'
      } (download ${id}, opens in a new tab)`;
      return url ? (
        <PlainLink href={url} openInNewTab aria-label={label}>
          Open file
        </PlainLink>
      ) : (
        'No file attached'
      );
    },
  }),
];

interface DownloadsTableProps {
  evaluationId: string | null;
}

/**
 * The paginated list, for one evaluation or for all of them.
 *
 * @param {DownloadsTableProps} props - Component props.
 * @param {string | null} props.evaluationId - The evaluation filtered to, or null.
 * @return {JSX.Element} The table.
 */
function DownloadsTable({ evaluationId }: DownloadsTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const query = useLegacyUnitEvaluationDownloads(page, pageSize, evaluationId);

  const handlePaginationChange = useCallback(
    ({ page: newPage, pageSize: newPageSize }: TableState) => {
      setPage(newPage);
      setPageSize(newPageSize);
    },
    []
  );

  return (
    <DataTable
      data={query.data?.downloads ?? []}
      columns={columns as ColumnDef<LegacyUnitEvaluationDownload>[]}
      getRowId={(row) => String(row.id)}
      // While the next page loads, DataTable keeps the previous page's rows on
      // screen, dimmed under its progress bar; it has no skeleton.
      isLoading={query.isPending || query.isPlaceholderData}
      error={query.error}
      emptyMessage={
        evaluationId
          ? `No exports were generated for evaluation ${evaluationId}.`
          : 'No exports were generated from the unit evaluation.'
      }
      persistenceKey="admin-legacy-unit-evaluation-downloads"
      controlledState={{ pagination: { page, pageSize } }}
      scroll={{
        mode: 'paginated',
        serverDriven: true,
        totalCount: query.data?.totalCount ?? 0,
      }}
      onPaginationChange={handlePaginationChange}
      config={{
        sorting: false,
        globalSearch: false,
        columnFilters: false,
        columnResizing: true,
        columnReordering: false,
        columnPinning: false,
        columnVisibility: false,
        sizeToContent: true,
      }}
    />
  );
}

/**
 * Read-only admin list of the exports generated from the retired unit evaluation. It
 * opens existing files and has no way to create one. Old links to the retired evaluation
 * pages redirect here with `?evaluation_id=`, which limits the list to that evaluation.
 *
 * @return {JSX.Element} The page.
 */
export function LegacyUnitEvaluationDownloadsPage() {
  const history = useHistory();
  const evaluationId = useEvaluationIdFilter();

  const clearFilter = useCallback(() => {
    history.push(history.location.pathname);
  }, [history]);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Legacy Unit Evaluation Downloads"
        actions={null}
      />
      <p>
        Exports from the retired unit evaluation. Read-only: these files can be
        opened, but no new ones can be generated.
      </p>
      {evaluationId && (
        <div className={styles.filterBanner}>
          <span>Showing exports for evaluation {evaluationId}</span>
          <Button kind="ghost" size="sm" onClick={clearFilter}>
            Show all exports
          </Button>
        </div>
      )}
      {/* Keyed so a change of filter starts again from page 1. */}
      <DownloadsTable key={evaluationId ?? 'all'} evaluationId={evaluationId} />
    </>
  );
}
